import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Alert
} from '@mui/material'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell
} from 'recharts'
import {
  CheckCircle,
  Schedule,
  Warning,
  Error as ErrorIcon
} from '@mui/icons-material'

import { formatCurrency, formatDate, formatInvoiceStatus } from '../../utils/formatters'
import { useAgingReport } from '../../hooks/useReports'

const AgingReport = ({ filters }) => {
  const { data, isLoading, error } = useAgingReport(filters)

  const agingLabels = {
    'current': 'Al día',
    '1-30': '1-30 días',
    '31-60': '31-60 días', 
    '61-90': '61-90 días',
    'over-90': '+90 días',
    'paid': 'Pagadas'
  }

  const agingColors = {
    'current': '#4caf50',
    '1-30': '#ff9800',
    '31-60': '#f57c00',
    '61-90': '#e65100',
    'over-90': '#d32f2f',
    'paid': '#2e7d32'
  }

  const agingIcons = {
    'current': <Schedule />,
    '1-30': <Warning />,
    '31-60': <Warning />,
    '61-90': <ErrorIcon />,
    'over-90': <ErrorIcon />,
    'paid': <CheckCircle />
  }

  if (error) {
    return (
      <Alert severity="error">
        Error cargando reporte de antigüedad: {error.message}
      </Alert>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Análisis de Antigüedad
          </Typography>
          <Skeleton variant="rectangular" height={400} />
        </CardContent>
      </Card>
    )
  }

  const summary = data?.summary || {}
  const buckets = data?.buckets || {}

  // Prepare chart data
  const chartData = Object.entries(summary)
    .filter(([key]) => key !== 'paid' && key !== 'totalOutstanding')
    .map(([key, value]) => ({
      name: agingLabels[key],
      amount: value.amount,
      count: value.count,
      color: agingColors[key]
    }))

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(summary).map(([key, value]) => {
          if (key === 'totalOutstanding') return null
          
          return (
            <Grid item xs={6} sm={4} md={2} key={key}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ color: agingColors[key], mb: 1 }}>
                    {agingIcons[key]}
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    {value.count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {agingLabels[key]}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={agingColors[key]}>
                    {formatCurrency(value.amount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      {/* Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Distribución por Antigüedad
          </Typography>
          
          <Box sx={{ width: '100%', height: 300, mt: 2 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tickFormatter={(value) => formatCurrency(value)}
                  fontSize={12}
                />
                <Tooltip 
                  formatter={(value, name) => [formatCurrency(value), 'Monto']}
                  labelFormatter={(label) => `Antigüedad: ${label}`}
                />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Detailed Tables by Bucket */}
      {Object.entries(buckets).map(([bucketKey, bucket]) => {
        if (!bucket.invoices || bucket.invoices.length === 0) return null
        
        return (
          <Card key={bucketKey} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ color: agingColors[bucketKey] }}>
                  {agingIcons[bucketKey]}
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {agingLabels[bucketKey]}
                </Typography>
                <Chip 
                  label={`${bucket.count} facturas - ${formatCurrency(bucket.amount)}`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Factura</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Emisión</TableCell>
                      <TableCell>Vencimiento</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell align="center">Días Vencido</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bucket.invoices.slice(0, 10).map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {invoice.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {invoice.client?.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(invoice.issueDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(invoice.dueDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(invoice.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            color={invoice.daysOverdue > 0 ? 'error.main' : 'text.primary'}
                            fontWeight={invoice.daysOverdue > 0 ? 600 : 400}
                          >
                            {invoice.daysOverdue > 0 ? invoice.daysOverdue : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatInvoiceStatus(invoice.visualStatus || invoice.status)}
                            color={
                              invoice.status === 'PAGADA' ? 'success' :
                              (invoice.visualStatus || invoice.status) === 'VENCIDA' ? 'error' : 'warning'
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {bucket.invoices.length > 10 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Mostrando 10 de {bucket.invoices.length} facturas
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )
      })}

      {/* Summary */}
      <Paper sx={{ p: 3, backgroundColor: 'background.default' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Resumen de Antigüedad
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Total pendiente de cobro:
            </Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {formatCurrency(summary.totalOutstanding || 0)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
                Facturas más críticas (+90 días):
            </Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {summary['over-90']?.count || 0} facturas
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

export default AgingReport
